import React from 'react';
import type { ISolutionReview } from '../../../../../types/common/marketplace';
import type { TextSkin } from '@wix/design-system';
import { Box, Text, TextButton } from '@wix/design-system';
import { ThumbsUp } from '@wix/wix-ui-icons-common';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import {
  deleteLikeToReview,
  getReview,
  postLikeToReview,
} from '../../../../../api/api';
import { composer } from '../../../../../component-decorators/composer';
import { biLogger } from '../../../../../bi';
import { useConfigContext } from '../../../../../config-context';
import { appMarketLikeUnlikeAppReview } from '@wix/bi-logger-market/v2';

interface IHelpfulReviewProps extends Partial<WithTranslationProps> {
  review?: ISolutionReview;
  appId: string;
}

interface TextUnderButtonProps {
  text: string;
  secondary: boolean;
  skinColor: TextSkin;
}

export const HelpfulReview = composer()
  .withTranslation()
  .compose(({ t, appId, review }: IHelpfulReviewProps) => {
    const { isMobile } = useConfigContext();

    const [showTextHelpfulReview, updateShowTextHelpfulReview] =
      React.useState(0);
    const [reviewInfo, updateReviewInfo] = React.useState(review);
    const [textUnderButton, updateTextUnderButton] =
      React.useState<TextUnderButtonProps>({
        text: 'solution.page.afterLikeOnReview',
        secondary: false,
        skinColor: 'success',
      });
    React.useEffect(() => {
      setTimeout(() => updateShowTextHelpfulReview(0), 5000);
    }, [showTextHelpfulReview]);

    const clickLikeReview = async (
      appId: string,
      reviewId: string,
      userOpinion,
    ) => {
      const sendBi = async (value: string) => {
        await biLogger.report(
          appMarketLikeUnlikeAppReview({
            helpful_click: value,
            appId,
          }),
        );
      };

      if (userOpinion?.id) {
        await deleteLikeToReview(appId, reviewId, userOpinion.id);
        updateReviewInfo(await getReview(appId, reviewId));
        await sendBi('unlike');
      } else {
        try {
          await postLikeToReview(appId, reviewId);
          updateReviewInfo(await getReview(appId, reviewId));
          updateShowTextHelpfulReview(1);
          await sendBi('like');
        } catch (error) {
          if (error.response.status === 401) {
            updateTextUnderButton({
              text: 'solution.page.tryingLikeNotLoggedIn',
              secondary: true,
              skinColor: 'standard',
            });
            updateShowTextHelpfulReview(1);
          }
        }
      }
    };

    return !isMobile ? (
      <Box align="right" marginBottom="35px" direction="vertical">
        <Box>
          <TextButton
            weight="thin"
            prefixIcon={<ThumbsUp />}
            dataHook="helpful-button"
            style={{ marginRight: '6px' }}
            as="button"
            onClick={() =>
              clickLikeReview(appId, reviewInfo.id, reviewInfo.userOpinion)
            }
          >
            {t('solution.page.helpfulReviews')}
          </TextButton>
          <Text
            skin="standard"
            secondary
            weight="thin"
            style={{ marginBottom: '6px' }}
            dataHook="total-likes"
          >
            {'(' + reviewInfo.totalLikes + ')'}
          </Text>
        </Box>
        <Box
          opacity={showTextHelpfulReview}
          transitionDuration="0.35s"
          transitionProperty="opacity"
          transitionTimingFunction="ease"
        >
          <Text
            secondary={textUnderButton.secondary}
            skin={textUnderButton.skinColor}
            weight="thin"
            size="tiny"
            dataHook="helpful-subtitle"
          >
            {t(textUnderButton.text)}
          </Text>
        </Box>
      </Box>
    ) : null;
  });
